import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Bid, BidStatus } from '../../_models/bid';
import { FieldPlanImport, importFieldPlan } from './bid-field-plan';
import { trackerFieldsFromBid } from './bid-tracker';

/** More waves than any tracker year has, so a broken chain can't loop forever. */
const MAX_WAVE_HOPS = 24;

/** What a tracker bid's clone becomes, chosen in the tracker clone prompt before the draft opens. */
export type TrackerCloneChoice = 'separate' | 'wave' | 'renewal';

/** `/bids/new` history state for a clone. The origin lets the draft name the source bid and restore
 *  its name for a wave or renewal. `cloneType` is set only for a tracker source; `fieldPlan` when the
 *  field plan was imported from a NAV project, already applied to `cloneSource`. */
export interface BidCloneState {
  cloneSource: Partial<Bid>;
  cloneOrigin: { id: string; number: string; name: string };
  cloneType?: TrackerCloneChoice;
  fieldPlan?: FieldPlanImport;
}

export function buildBidCloneState(full: Bid, cloneType?: TrackerCloneChoice, fieldPlan?: FieldPlanImport | null): BidCloneState {
  const source = buildBidCloneSource(full);
  return {
    cloneSource: fieldPlan ? { ...source, ...importFieldPlan(source, fieldPlan) } : source,
    cloneOrigin: { id: full.id, number: full.number, name: full.name },
    cloneType,
    fieldPlan: fieldPlan ?? undefined,
  };
}

/** A bid that was Closed Won and made NAV projects, so its clone can import how the project ran. */
export function hasNavProject(bid: Bid | null | undefined): boolean {
  return bid?.status === BidStatus.Sold && !!bid.convertedAt;
}

/** Toast for a plain (non-tracker) copy. A tracker clone shows none: the draft's own note says what it is. */
export function bidCopyOpenedMessage(number: string): string {
  return `Copy of bid #${number} opened. Click Create Bid to save it.`;
}

/** Builds the pre-populated draft the create flow opens with. Status isn't copied, so a bid in any
 *  status can be the source. A tracker source keeps its flag, frequency and tag; the tracker clone
 *  prompt then settles the name, dates and tag for a wave, a renewal or a separate new bid. */
export function buildBidCloneSource(full: Bid): Partial<Bid> {
  const tracker = trackerFieldsFromBid(full);
  return {
    name: `${full.name} (Copy)`,
    clientId: full.clientId,
    clientContactId: full.clientContactId,
    accountOwnerId: full.accountOwnerId,
    clientBudget: full.clientBudget,
    startDate: full.startDate,
    endDate: full.endDate,
    projectTypeId: full.projectTypeId,
    specText: full.specText,
    serviceSelections: full.serviceSelections,
    serviceBids: full.serviceBids,
    samplePlan: full.samplePlan,
    structure: full.structure,
    isTracker: tracker.isTracker,
    trackerFrequency: tracker.trackerFrequency,
    trackerTag: tracker.trackerTag,
    // A wave or renewal keeps its tracker's ID; the tracker clone prompt clears it for a separate bid.
    trackerId: full.trackerId ?? null,
    ...cloneVendorBids(full),
  };
}

/** Keeps the vendor selection but strips transactional data: quotes, sent flags, allocations, notes,
 *  fp-exclusions. */
function cloneVendorBids(full: Bid): Pick<Bid, 'vendors' | 'vendorScenarios' | 'maBidMode'> {
  return {
    vendors: (full.vendors ?? []).map(v => ({
      vendorId: v.vendorId,
      sent: false,
      sentAt: null,
      countries: v.countries,
    })),
    vendorScenarios: (full.vendorScenarios ?? []).map(s => ({
      key: s.key,
      sellCpi: s.sellCpi,
      optionName: s.optionName,
      bids: [],
    })),
    maBidMode: full.maBidMode,
  };
}

/**
 * The bid that started a tracker's current year: the new tracker or renewal its waves were cloned
 * from, found by following each wave back to its source. Null when a wave has no recorded source
 * (cloned before that link was stored) or a bid can't be loaded.
 */
export function trackerYearStart(bid: Bid, load: (id: string) => Observable<Bid>, hops = 0): Observable<Bid | null> {
  if (bid.trackerCloneType !== 'wave') return of(bid);
  if (!bid.clonedFromBidId || hops >= MAX_WAVE_HOPS) return of(null);
  return load(bid.clonedFromBidId).pipe(
    switchMap(prev => trackerYearStart(prev, load, hops + 1)),
    catchError(() => of(null)),
  );
}

/**
 * A renewal's pricing fields from last year's full year: the option the client bought, at the figures
 * confirmed at Closed Won (the stored copy holds only that option). Vendor quotes are cleared as on any
 * clone, since every year is re-bid. Null when the year's bid has no saved full year to start from.
 */
export function renewalFieldsFromYearStart(yearStart: Bid): Pick<Bid, 'samplePlan' | 'commitments' | 'vendorScenarios' | 'serviceSelections' | 'serviceBids'> | null {
  const source = yearStart.fullYearPricing?.source;
  if (!source?.samplePlan?.length) return null;
  return {
    samplePlan: source.samplePlan,
    commitments: source.commitments ?? [],
    vendorScenarios: (source.vendorScenarios ?? []).map(s => ({ key: s.key, sellCpi: s.sellCpi, optionName: s.optionName, bids: [] })),
    serviceSelections: source.serviceSelections,
    serviceBids: source.serviceBids,
  };
}
